// src/pages/BetaAgreement.tsx
import React from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RotateCcw, Download } from "lucide-react";
import { usePDF } from "react-to-pdf";
import Navbar from "@/components/Navbar";
import CallToActionFooter from "@/components/Footer";
import footerlogo from "@/assets/mylistiq-logo-updated.png";


const agreementSchema = z.object({
  full_name: z.string().min(1, "Full name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(5, "Phone number is required"),
  signature: z.string().min(1, "Signature (typed name) is required"),
  signed_date: z.string().min(1, "Date is required"),
});

type AgreementFormData = z.infer<typeof agreementSchema>;

const BetaAgreement: React.FC = () => {
  const methods = useForm<AgreementFormData>({
    resolver: zodResolver(agreementSchema),
    defaultValues: {
      signed_date: "2026-01-09", // Current date as per system
    },
  });

  const { handleSubmit, watch, formState: { errors } } = methods;
  const formData = watch();

  const { toPDF, targetRef } = usePDF({
    filename: `MyListIQ_Beta_Agreement_${formData.full_name?.replace(/\s+/g, "_") || "Tester"}.pdf`,
  });

  const onSubmit = () => {
    toPDF();
  };

  return (
    
    <FormProvider {...methods}>
      {/* Header */}
      {/* <header className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/mylistiq_logo.png"
              alt="MyListIQ Logo"
              className="h-12 w-auto"
            />
            <h1 className="text-2xl font-bold">MyListIQ Beta Tester Agreement</h1>
          </div>
          <div className="text-sm opacity-90">
            Version: Beta v1.0
          </div>
        </div>
      </header> */}

      <Navbar />

      <div className="min-h-screen bg-gradient-to-b from-blue-10 to-indigo-50 py-12 px-4">
        <div className="max-w-5xl mx-auto space-y-10">

          {/* Instructions */}
          <Alert className="border-l-4 border-indigo-600 bg-blue-50">
            <AlertDescription className="text-base">
              <strong className="block mb-3 text-xl text-indigo-900">Next Steps</strong>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                <li>Fill in your details and type your name as signature below.</li>
                <li>Click <strong>“Download Signed Agreement (PDF)”</strong> — the file will download automatically.</li>
                <li>Email the PDF to <span className="font-bold text-red-600">contracts@mylistiq.com</span></li>
              </ol>
            </AlertDescription>
          </Alert>

          {/* Printable Agreement - Beautiful Design */}
          <div ref={targetRef} className="bg-white rounded-2xl shadow-2xl overflow-hidden">

            {/* Agreement Header Inside PDF */}
            <div className="bg-gradient-to-r from-indigo-700 to-blue-800 text-white p-10 text-center">
              {/* <img
                src="https://mylistiq-production.s3.us-east-1.amazonaws.com/static/image/mylistiq_logo.png"
                alt="MyListIQ Logo"
                className="h-16 mx-auto mb-4"
              /> */}

                <img
                src={footerlogo}
                alt="MyListIQ - Easy. Affordable. Smarter."
                className="h-12 w-auto"
              />
              <h1 className="text-4xl font-bold">MyListIQ Beta Tester Agreement</h1>
              <p className="mt-2 opacity-90">Confidential • Beta Program</p>
            </div>

            <div className="p-10 space-y-8 text-gray-800 leading-relaxed text-base">
              <p>
                This Beta Tester Agreement (“Agreement”) is between <strong>MyListIQ</strong> (“MyListIQ,” “we,” “us”) and you (“Beta Tester,” “you”). 
                The MyListIQ beta program allows selected users to test early versions of the MyListIQ platform and provide feedback to help improve functionality, usability, and overall experience prior to public launch.
              </p>

              <p>
                Beta access will be provided once you provide your full name, email address, and telephone number, which will be used solely for beta testing communication and support. 
                As a thank-you for participating, you will receive <strong>two (2) months of a free Pro subscription</strong> once the MyListIQ solution officially goes live. 
                This incentive is non-transferable and has no cash value.
              </p>

              <p>
                You agree to use the beta platform in good faith for testing and evaluation purposes only and not to misuse, disrupt, or share beta access with unauthorized parties.
              </p>

              <p>
                You retain ownership of any content you upload during beta testing, including listing information, images, or text. 
                <strong>All content created or uploaded within the beta platform will be removed prior to the solution going live</strong> and will not carry over into the production environment.
              </p>

              <p>
                Your feedback, including feature ratings and checklist responses, is truly valued and encouraged. 
                Feedback is voluntary and does not create ownership rights or entitle you to compensation. 
                MyListIQ may freely use feedback to improve the platform.
              </p>

              <p>
                During the beta period, you may be exposed to non-public features, designs, workflows, or information. 
                You agree to keep all beta-related materials and experiences confidential and not share them publicly without written permission from MyListIQ.
              </p>

              <p>
                By participating in the beta program, you consent to receive beta-related communications from MyListIQ via email or phone.
              </p>

              {/* Signature Box - Beautifully Styled */}
              <div className="bg-gray-50 border-2 border-dashed border-indigo-300 rounded-2xl p-8 mt-12">
                <h2 className="text-2xl font-bold text-indigo-900 mb-8 text-center">Beta Tester Signature</h2>

                <div className="grid md:grid-cols-2 gap-8 text-lg">
                  <div>
                    <strong className="block text-gray-600 mb-1">Full Name</strong>
                    <div className="font-semibold text-gray-900">
                      {formData.full_name || "________________________________________________"}
                    </div>
                  </div>
                  <div>
                    <strong className="block text-gray-600 mb-1">Email Address</strong>
                    <div className="font-semibold text-gray-900">
                      {formData.email || "________________________________________________"}
                    </div>
                  </div>
                  <div>
                    <strong className="block text-gray-600 mb-1">Telephone Number</strong>
                    <div className="font-semibold text-gray-900">
                      {formData.phone || "________________________________________________"}
                    </div>
                  </div>
                  <div>
                    <strong className="block text-gray-600 mb-1">Date</strong>
                    <div className="font-semibold text-gray-900">
                      {formData.signed_date || "________________________________________________"}
                    </div>
                  </div>
                </div>

                <div className="mt-10 pt-8 border-t-2 border-indigo-200">
                  <strong className="block text-gray-600 mb-2 text-lg">Signature (Typed Name)</strong>
                  <div className="font-cursive text-3xl text-indigo-700 font-bold italic">
                    {formData.signature || "________________________________________________"}
                  </div>
                </div>
              </div>

              {/* Footer Inside PDF */}
              <footer className="text-center text-sm text-gray-500 mt-12 pt-8 border-t">
                <div className="flex justify-center gap-8 flex-wrap">
                  <span><strong>Version:</strong> Beta v1.0</span>
                  <span><strong>© MyListIQ 2026</strong></span>
                  <span><strong>Beta Confidential</strong></span>
                </div>
              </footer>
            </div>
          </div>

          {/* Form Below Agreement */}
          <div className="bg-white rounded-2xl shadow-xl p-10">
            <h2 className="text-3xl font-bold text-center text-indigo-900 mb-8">Complete Your Agreement</h2>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div>
                <Label htmlFor="full_name" className="text-lg">Full Name</Label>
                <Input {...methods.register("full_name")} placeholder="Enter your full name" className="mt-2" />
                {errors.full_name && <p className="text-sm text-red-600 mt-1">{errors.full_name.message}</p>}
              </div>

              <div>
                <Label htmlFor="email" className="text-lg">Email Address</Label>
                <Input type="email" {...methods.register("email")} placeholder="you@example.com" className="mt-2" />
                {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <Label htmlFor="phone" className="text-lg">Telephone Number</Label>
                <Input type="tel" {...methods.register("phone")} placeholder="(555) 123-4567" className="mt-2" />
                {errors.phone && <p className="text-sm text-red-600 mt-1">{errors.phone.message}</p>}
              </div>

              <div>
                <Label htmlFor="signature" className="text-lg">Signature (Type Your Full Name)</Label>
                <Input {...methods.register("signature")} placeholder="Type your name to sign" className="mt-2 text-lg font-medium" />
                {errors.signature && <p className="text-sm text-red-600 mt-1">{errors.signature.message}</p>}
              </div>

              <div>
                <Label htmlFor="signed_date" className="text-lg">Date</Label>
                <Input type="date" {...methods.register("signed_date")} className="mt-2" />
                {errors.signed_date && <p className="text-sm text-red-600 mt-1">{errors.signed_date.message}</p>}
              </div>

              <div className="flex flex-wrap justify-center gap-6 pt-8">
                <Button type="submit" size="lg" className="bg-gradient-to-r from-indigo-600 to-blue-700 hover:from-indigo-700 hover:to-blue-800 text-lg px-10">
                  <Download className="w-6 h-6 mr-3" />
                  Download Signed Agreement (PDF)
                </Button>
                <Button type="button" variant="outline" size="lg" onClick={() => methods.reset()}>
                  <RotateCcw className="w-6 h-6 mr-3" />
                  Clear Form
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Page Footer */}
      <CallToActionFooter />
    </FormProvider>
  );
};

export default BetaAgreement;